import type { ThroughputBucketDto } from "@/api/endpoints";

export type ChartMode = "grouped" | "overlay" | "line";

const COLORS: Record<string, string> = {
  started: "var(--gw-blue-500)",
  completed: "var(--gw-success-500)",
  backlog: "var(--gw-warning-500)",
};

/** Short month label from a "YYYY-MM" period, e.g. "Mar 26". */
export function shortMonth(period: string): string {
  const [y, m] = period.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = Number(m) - 1;
  const name = idx >= 0 && idx < 12 ? names[idx] : m;
  return `${name} ${y?.slice(2) ?? ""}`.trim();
}

type Series = "started" | "completed" | "backlog";
const valueOf = (m: ThroughputBucketDto, s: Series): number =>
  s === "started" ? m.startedCount : s === "completed" ? m.completedCount : m.openAtEnd;

interface Props {
  months: ThroughputBucketDto[];
  mode: ChartMode;
  showBacklog: boolean;
  onPick?: (period: string) => void;
  activePeriod?: string;
}

export function ThroughputChart({ months, mode, showBacklog, onPick, activePeriod }: Props) {
  const series: Series[] = ["started", "completed", ...(showBacklog ? (["backlog"] as Series[]) : [])];
  const max = Math.max(1, ...months.flatMap((m) => series.map((s) => valueOf(m, s))));
  const H = 260;

  if (mode === "line") {
    const n = months.length;
    const padL = 4;
    const padR = 4;
    const xAt = (i: number) => (n <= 1 ? 50 : padL + (i / (n - 1)) * (100 - padL - padR));
    const yAt = (v: number) => 100 - (v / max) * 92 - 4;
    const poly = (key: Series) => months.map((m, i) => `${xAt(i)},${yAt(valueOf(m, key))}`).join(" ");
    return (
      <div className="tp-chart">
        <div className="tp-line-wrap" style={{ height: H }}>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="tp-line-svg">
            {[0.25, 0.5, 0.75].map((g) => (
              <line key={g} x1="0" x2="100" y1={100 - g * 92 - 4} y2={100 - g * 92 - 4} className="tp-grid" />
            ))}
            {series.map((s) => (
              <polyline
                key={s}
                points={poly(s)}
                fill="none"
                stroke={COLORS[s]}
                strokeWidth="0.9"
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
                strokeDasharray={s === "backlog" ? "3 2" : undefined}
              />
            ))}
          </svg>
        </div>
        <div className="tp-xaxis">
          {months.map((m) => (
            <div
              key={m.period}
              className={"tp-xlabel" + (m.period === activePeriod ? " on" : "")}
              onClick={() => onPick?.(m.period)}
            >
              {shortMonth(m.period)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const pct = (v: number) => `${(v / max) * 100}%`;
  return (
    <div className="tp-chart">
      <div className="tp-bars" style={{ height: H }}>
        {months.map((m) => (
          <div
            key={m.period}
            className={"tp-col" + (m.period === activePeriod ? " on" : "")}
            onClick={() => onPick?.(m.period)}
            title={`${shortMonth(m.period)} · click to drill`}
          >
            {mode === "overlay" ? (
              <div className="tp-overlay">
                <div className="tp-bar started" style={{ height: pct(m.startedCount) }}>
                  <span className="tp-val">{m.startedCount || ""}</span>
                </div>
                <div className="tp-bar completed front" style={{ height: pct(m.completedCount) }}>
                  <span className="tp-val on-bar">{m.completedCount || ""}</span>
                </div>
                {showBacklog && <div className="tp-bar backlog edge" style={{ height: pct(m.openAtEnd) }} />}
              </div>
            ) : (
              <div className="tp-grouped">
                {series.map((s) => (
                  <div key={s} className={"tp-bar " + s} style={{ height: pct(valueOf(m, s)) }}>
                    <span className="tp-val">{valueOf(m, s) || ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="tp-xaxis">
        {months.map((m) => (
          <div
            key={m.period}
            className={"tp-xlabel" + (m.period === activePeriod ? " on" : "")}
            onClick={() => onPick?.(m.period)}
          >
            {shortMonth(m.period)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ThroughputLegend({ showBacklog }: { showBacklog: boolean }) {
  const items = [
    { c: COLORS.started, l: "Started (by created date)" },
    { c: COLORS.completed, l: "Completed (by approved date)" },
    ...(showBacklog ? [{ c: COLORS.backlog, l: "Open at month end (backlog)" }] : []),
  ];
  return (
    <div className="tp-legend">
      {items.map((it) => (
        <span key={it.l} className="tp-legend-item">
          <span className="tp-swatch" style={{ background: it.c }} /> {it.l}
        </span>
      ))}
    </div>
  );
}
